import React from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import { YStack, XStack, Separator, useMedia } from 'tamagui'

import { UserCard } from '@newComponents/User/UserCard'
import { Grid } from '@newComponents/Grid'
import { Button } from '@newComponents/Button/Button'
import { H2 } from '@newComponents/Text'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersPresentersSection_guild$key } from './__generated__/GuildMembersPresentersSection_guild.graphql'

function useItemsPerRow() {
  const media = useMedia()
  if (media.xs) return INITIAL_COUNTS[0]
  if (media.sm) return INITIAL_COUNTS[1]
  if (media.md) return INITIAL_COUNTS[2]
  if (media.lg) return INITIAL_COUNTS[3]
  return INITIAL_COUNTS[4]
}

export const GuildMembersPresentersSection = ({
  guild,
}: {
  guild: GuildMembersPresentersSection_guild$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersPresentersSection_guild on Guild
      @refetchable(queryName: "GuildMembersPresentersSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        presenters(first: $count, after: $cursor)
          @connection(key: "GuildMembersPresentersSection_guild_presenters") {
          edges {
            node {
              ...UserCardNew_user
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const itemsPerRow = useItemsPerRow()
  const media = useMedia()

  if (data.presenters.edges.length === 0) {
    return null
  }

  if (media.xs) {
    return (
      <YStack gap='$4'>
        <XStack gap='$4' alignItems='center'>
          <H2 size='$4' color='$colorMuted' fontWeight='$5'>
            Presenters
          </H2>
          <Separator flex={1} borderColor='$borderColor' />
        </XStack>

        {data.presenters.edges.map((edge) => (
          <UserCard
            key={edge.cursor}
            user={edge.node}
          />
        ))}

        {hasNext && (
          <Button
            secondary
            onPress={() => {
              if (isLoadingNext) return
              loadNext(3)
            }}
          >
            <Button.Text secondary>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Button.Text>
          </Button>
        )}
      </YStack>
    )
  }

  const presenterGroups = data.presenters.edges.reduce<
    Array<Array<(typeof data.presenters.edges)[number]>>
  >((memo, presenter, index) => {
    if (index % itemsPerRow === 0) {
      memo.push([presenter])
    } else {
      memo[memo.length - 1].push(presenter)
    }

    return memo
  }, [])

  return (
    <YStack gap='$4'>
      <XStack gap='$4' alignItems='center'>
        <H2 size='$4' color='$colorMuted' fontWeight='$5'>
          Presenters
        </H2>
        <Separator flex={1} borderColor='$borderColor' />
      </XStack>

      <YStack gap='$4'>
        {presenterGroups.map((group, index) => {
          return (
            <XStack key={index}>
              <Grid gap='$4'>
                {group.map((edge) => (
                  <Grid.Column key={edge.cursor}>
                    <UserCard
                      user={edge.node}
                      expand
                    />
                  </Grid.Column>
                ))}

                {Array.from({
                  length: itemsPerRow - group.length,
                }).map((_, i) => (
                  <Grid.Column key={i} />
                ))}
              </Grid>
            </XStack>
          )
        })}
      </YStack>

      {hasNext && (
        <Button
          secondary
          onPress={() => {
            if (isLoadingNext) return
            loadNext(3)
          }}
        >
          <Button.Text secondary>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Button.Text>
        </Button>
      )}
    </YStack>
  )
}
